import React from 'react';
import { Tooltip, CircularProgress } from '@mui/material';
import {
  MUIDataTableColumn,
  MUIDataTableColumnDef,
  DisplayData,
} from 'mui-datatables';
import type { TFunction } from 'i18next';
import { DataTableStatisticDto } from './Dto/DataTableStatisticDto';
import { StyledIconButton, StyledToolbarContainer } from './StatisticsDataTable.styles';
import { CloudDownload, WorkspacePremium } from '@mui/icons-material';

type RowShape = { data: any[]; dataIndex: number };

const normalizeColumns = (cols: MUIDataTableColumnDef[]): MUIDataTableColumn[] =>
  cols.map((c) =>
    typeof c === 'string'
      ? ({ name: c, label: c, options: {} } as MUIDataTableColumn)
      : c
  );

const isDisplayed = (c: MUIDataTableColumn) => {
  const d = c.options?.display;
  return d !== 'excluded' && d !== false;
};

export const CustomStatisticsToolbar: React.FC<{
  selectedRows: { data: Array<{ index: number; dataIndex: number }> };
  displayData: DisplayData;
  adjustedData: DataTableStatisticDto[];
  columns: MUIDataTableColumnDef[];
  t: TFunction;
  onRequestDownload: (pending: { columns: MUIDataTableColumn[]; rows: RowShape[] }) => void;
  onGenerateCertificate: (lessonId: string, userIds: string[]) => void | Promise<void>;
  generatingCertificate: boolean;
}> = ({
  selectedRows,
  displayData,
  adjustedData,
  columns,
  t,
  onRequestDownload,
  onGenerateCertificate,
  generatingCertificate,
}) => {
  const normalized = React.useMemo(() => normalizeColumns(columns), [columns]);
  const displayedColumns = React.useMemo(
    () => normalized.filter(isDisplayed),
    [normalized]
  );

  const buildSelectedRowShapes = (): RowShape[] => {
    return selectedRows.data.map(({ dataIndex }) => {
      const original = adjustedData[dataIndex] as any;
      const values = displayedColumns.map((col) => {
        const fullIdx = normalized.indexOf(col);
        if (Array.isArray(original)) {
          return original[fullIdx];
        }
        return original?.[col.name] ?? '';
      });

      return { data: values, dataIndex };
    });
  };

  const handleExportSelected = () => {
    onRequestDownload({ columns: displayedColumns, rows: buildSelectedRowShapes() });
  };

  const selectedDtos = selectedRows.data
    .map(({ dataIndex }) => adjustedData[dataIndex])
    .filter(Boolean);
  const distinctLessonIds = Array.from(new Set(selectedDtos.map((dto) => dto.lessonId)));
  const singleLessonId = distinctLessonIds.length === 1 ? distinctLessonIds[0] : null;
  const certificateDisabled = !singleLessonId || generatingCertificate;

  const handleGenerateCertificate = () => {
    if (!singleLessonId) {
      return;
    }
    const userIds = Array.from(new Set(selectedDtos.map((dto) => dto.userId)));
    onGenerateCertificate(singleLessonId, userIds);
  };

  return (
    <StyledToolbarContainer>
      <Tooltip title={t('dataTable.downloadSelectedCsv')}>
        <StyledIconButton aria-label="download-selected" onClick={handleExportSelected} >
          <CloudDownload />
        </StyledIconButton>
      </Tooltip>
      <Tooltip title={t('trainingReport.generateCertificateCsv')}>
        <span>
          <StyledIconButton
            aria-label="generate-certificate-csv"
            onClick={handleGenerateCertificate}
            disabled={certificateDisabled}
          >
            {generatingCertificate ? <CircularProgress size={22} /> : <WorkspacePremium />}
          </StyledIconButton>
        </span>
      </Tooltip>
    </StyledToolbarContainer>
  );
};
